import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { NewsVoiceProvider } from "@/context/NewsVoiceContext";
import { WatchlistProvider } from "@/context/WatchlistContext";
import { LivePlayerProvider } from "@/context/LivePlayerContext";
import { hasAccess, isAdmin } from "@/lib/access";
import { Layout } from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Agent from "@/pages/Agent";
import News from "@/pages/News";
import Calendrier from "@/pages/Calendrier";
import Macro from "@/pages/Macro";
import MacroOverview from "@/pages/MacroOverview";
import Explorer from "@/pages/Explorer";
import Trends from "@/pages/Trends";
import Account from "@/pages/Account";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Pricing from "@/pages/Pricing";
import Admin from "@/pages/Admin";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Landing from "@/pages/Landing";

function FullLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0C10] text-slate-400">
      <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
    </div>
  );
}

function AppShell() {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <NewsVoiceProvider>
      <WatchlistProvider>
        <LivePlayerProvider>
          <Layout><Outlet /></Layout>
        </LivePlayerProvider>
      </WatchlistProvider>
    </NewsVoiceProvider>
  );
}

function PlanRoute({ minPlan, children }) {
  const { user } = useAuth();
  if (!hasAccess(user, minPlan)) return <Navigate to="/tarifs" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (!isAdmin(user)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/cgu" element={<Terms />} />
      <Route path="/confidentialite" element={<Privacy />} />
      <Route element={<AppShell />}>
        <Route path="/dashboard" element={<PlanRoute minPlan="basic"><Dashboard /></PlanRoute>} />
        <Route path="/macro-overview" element={<PlanRoute minPlan="premium"><MacroOverview /></PlanRoute>} />
        <Route path="/agent" element={<PlanRoute minPlan="premium"><Agent /></PlanRoute>} />
        <Route path="/news" element={<PlanRoute minPlan="basic"><News /></PlanRoute>} />
        <Route path="/calendrier" element={<PlanRoute minPlan="free"><Calendrier /></PlanRoute>} />
        <Route path="/macro" element={<PlanRoute minPlan="premium"><Macro /></PlanRoute>} />
        <Route path="/tendances" element={<PlanRoute minPlan="premium"><Trends /></PlanRoute>} />
        <Route path="/donnees" element={<PlanRoute minPlan="premium"><Explorer /></PlanRoute>} />
        <Route path="/tarifs" element={<Pricing />} />
        <Route path="/compte" element={<Account />} />
        <Route path="/paiement/succes" element={<PaymentSuccess />} />
        <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <AppRouter />
            <Toaster position="top-right" theme="dark" />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
